import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ContentService } from '@blk/cwp-ng-framework';
import { DzGridConstants } from '@blk/daisy/util';
import { GlobalAnalyticsMapperUtil, TealiumAnalyticsService, TealiumConstants } from '@ddt-web/analytics';
import { TabWithRoute } from '@ddt-web/common';
import { ApplicationPathConstants, ViewCategory } from '@ddt-web/config';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { ErrorPopupService, PopupDialogAction, PopupDialogActionOptions, PopupDialogObject, PopupDialogOptions } from '@ddt-web/error-handler';
import { MarketViewConfigService } from '@ddt-web/market-view';
import { ApplicationStateStoreProxyService } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { MeQueryParamKey, UrlRedirectService, UrlService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import * as find from 'lodash/find';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, switchMap } from 'rxjs/operators';

import { ModelsBrowseStoreConfig } from '../config/store.config';
import { FamilyTypes } from '../enums/family-types.enum';
import { ModelFamily } from '../models';
import { ModelsBrowseDataService } from '../services/models-browse-data.service';
import { ModelsHoldingsExcelDownloadService } from '../services/models-holdings-excel-download.service';
import { FamiliesUtil } from '../utils/families.util';

@UntilDestroy()
@Component({
	selector: 'mb-family-details-container',
	templateUrl: './family-details-container.component.html',
	styleUrls: ['./family-details-container.component.scss']
})
export class FamilyDetailsContainerComponent implements OnInit {
	@InjectConfig(ModelsExperienceConfigKey.SHOW_CMS_PDF_MODULARITY) private readonly showCmsPdfModularity: boolean;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_DOWNLOAD_HOLDINGS_BUTTON) public readonly showDownloadHoldings: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.FAMILY_DETAILS_TAB) private readonly familyDetailsTabConfig: Array<TabWithRoute>;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_PDF_MODULARITY) private readonly showPdfModularity: boolean = true;
	@InjectConfig(ModelsExperienceConfigKey.SHOW_ESG_PDF_PAGES) private readonly showEsgPdfPages: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.PERFORMANCE_COMMENTARY_ENABLED_FOR_CMS) private readonly isPerfCommentaryEnabledForCms = false;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_DOWNLOAD_HOLDINGS_MODULARITY) public readonly enableDownloadHoldingsModularity: boolean = true;

	public familyDetailsTabs: Array<TabWithRoute>;
	public visibleDetailsTabs: Array<TabWithRoute>;
	public familyName: string;
	public showDownloadPdfButton: boolean;
	public familyId: string;
	public userFilterName: string;
	public isBlkModel: boolean = false;
	public tealiumConstants = TealiumConstants;
	public modelSubscriptLabel: string;
	public modelExternalName: string;
	public readonly backToAvailableModelsRouterLink: string = `/${ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FIND_AND_COMPARE}`;
	private readonly isModelsRegroupingEnabled: boolean = true;
	public isEsgFamily: boolean = false;
	public hasCMSModels: boolean = false;
	public familyTypes = FamilyTypes;

	public navHeaderClass: { [name: string]: string } = {
		appName: 'dz-col-18 dz-col-md-10 dz-col-lg-6',
		actionButtons: 'dz-col-6 dz-col-md-14 dz-col-lg-18'
	};

	constructor(
		private contentService: ContentService,
		private storeService: StoreService,
		private urlService: UrlService,
		private urlRedirectService: UrlRedirectService,
		private router: Router,
		private modelsBrowseDataService: ModelsBrowseDataService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private tealiumAnalyticsService: TealiumAnalyticsService,
		private modelsHoldingsExcelDownloadService: ModelsHoldingsExcelDownloadService,
		private marketViewConfigService: MarketViewConfigService,
		private errorPopupService: ErrorPopupService
	) { }

	@HostListener('window:resize', ['$event'])
	private onResize(): void {
		this.adjustFamilyDetailsTabs();
	}

	public ngOnInit(): void {
		this.showDownloadPdfButton = this.contentService.getBoolean('SHOW_DOWNLOAD_PDF_BUTTON', true);
		this.familyId = this.urlService.getModelFamilyId();
		const groupId = this.urlService.getModelGroupId();
		this.userFilterName = this.urlService.getParam('userFilterName');
		if (this.urlService.getSelectedModelIds()) {
			this.router.navigate([], {
				queryParams: {
					[MeQueryParamKey.SELECTED_MODEL_IDS]: null
				},
				queryParamsHandling: 'merge'
			});
		}

		if (!this.familyId) {
			this.router.navigate(['/']);
			return;
		}

		// track family id using the portfolioIds
		GlobalAnalyticsMapperUtil.setPortfolioIds(this.familyId);

		this.storeService.selectAll(ModelsBrowseStoreConfig.families).
			pipe(
				untilDestroyed(this),
				filter(families => !families.isLoading && !isEmpty(families.entities))
			).
			subscribe(families => {
				const modelFamilies: Array<ModelFamily> = Object.values(families.entities);
				const { categories } = FamiliesUtil.generateCategoriesModelLevel(families);
				this.hasCMSModels = categories.length > 1 ? true : false;

				const modelFamiliesObj = find(modelFamilies, ['id', this.familyId]);
				this.familyName = get(modelFamiliesObj, 'description', '');
				this.isBlkModel = get(modelFamiliesObj, 'subType', '') === 'BLK';
				this.isEsgFamily = get(modelFamiliesObj, 'preferences', '') === 'esg';
				this.modelSubscriptLabel = get(modelFamiliesObj, 'modelSubscriptLabel', '');
				this.modelExternalName = groupId !== SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP ? get(modelFamiliesObj, 'modelExternalName', '') : null;
				this.applicationStateStoreProxyService.updateGenerateReportData({ additionalHeaderInfo: !isEmpty(this.modelExternalName) ? this.modelExternalName : this.familyName });
				this.applicationStateStoreProxyService.updateMeSelectedModelsData(
					{ routedFrom: ApplicationPathConstants.MODELS_EXPERIENCE_PATHS.FAMILY }
				);
				// TODO Waris: temporary error handling, permanent fix to be implemented as a part of migration Q3 2022
				const missingShortName = get(modelFamiliesObj, 'portfolios', []).some(portfolio => !portfolio.shortName);
				if (missingShortName && !this.isModelsRegroupingEnabled) {
					this.showError();
				}

				this.familyDetailsTabs = cloneDeep(this.familyDetailsTabConfig);

				const dataPathMappingToTabRoute = {
					'performance-commentary': 'performanceCommentary',
					'commentary': 'commentaryTypeRef',
					'additional-resources': 'documentIds',
					'esg-metrics': 'esg-metrics'
				};

				this.familyDetailsTabs.forEach(tab => {
					if (Object.keys(dataPathMappingToTabRoute).includes(tab.route)) {
						this.handleTabVisibility(modelFamiliesObj,
							dataPathMappingToTabRoute[tab.route],
							{ route: tab.route, label: tab.label }
						);
					}
				});

				this.adjustFamilyDetailsTabs();
				this.sendTrackingEvent();
			});

	}

	private adjustFamilyDetailsTabs(): void {
		if (window.innerWidth >= DzGridConstants.BREAKPOINTS.lg) {
			this.visibleDetailsTabs = this.familyDetailsTabs;
		} else {
			this.visibleDetailsTabs = this.familyDetailsTabs.filter(tab => tab.route.indexOf('performance') < 0);
			if (this.router.url.indexOf('performance') > -1) {
				this.router.navigate(['family/latest-holdings'], { queryParamsHandling: 'merge' }); // navigate to latest-holdings if performance tab is hidden for lower screen sizes
			}
		}
	}

	private handleTabVisibility(family: ModelFamily, dataPath: string, tabRoute: TabWithRoute): void {
		let showTab = false;
		const data = get(family, dataPath);
		const isFactsSheetsLink = this.contentService.getBoolean(`${this.familyId.toUpperCase()}_FACTS_SHEET_HYPERLINK`, false);
		if (dataPath === 'commentaryTypeRef' && this.contentService.get(data, '')) {
			showTab = true;
		}
		if (dataPath === 'documentIds' && (!isEmpty(data) || isFactsSheetsLink)) {
			showTab = true;
		}
		if (dataPath === 'performanceCommentary' && (this.isBlkModel || this.isPerfCommentaryEnabledForCms)) {
			showTab = true;
		}
		if (dataPath === 'esg-metrics' && this.isEsgFamily) {
			showTab = true;
		}
		if (!showTab) {
			this.familyDetailsTabs = this.familyDetailsTabs.filter(tab => tab.route !== tabRoute.route);
			// Copy object to trigger ngOnChanges, filter won't trigger it
			this.familyDetailsTabs = cloneDeep(this.familyDetailsTabs);
		}
	}

	public sendTrackingEvent(): void {
		GlobalAnalyticsMapperUtil.setSiteSubSubSection(TealiumConstants.MODELS_EXPERIENCE_SUB_SUB_SECTION_NAMES.FAMILY_DETAILS);
		GlobalAnalyticsMapperUtil.setPageName(TealiumConstants.MODELS_EXPERIENCE_SUB_SUB_SECTION_NAMES.FAMILY_DETAILS);
		this.tealiumAnalyticsService.triggerPageView({ 'tool_name': TealiumConstants.MODELS_EXPERIENCE_EVENTS.MODELS_EXPERIENCE_APP_NAME });
	}

	/**
	 * Fetches portfolio analysis for the selected family and updates the store
	 * Fetches performance data if absent and updates it in the store
	 * Waits for the store to be loaded and  updates the report config store before redirecting
	 **/
	public generateAdvisorReport(): void {
		combineLatest([
			this.modelsBrowseDataService.fetchFamilyPortfolioAnalysis(),
			this.storeService.selectAll(ModelsBrowseStoreConfig.familyPerformance),
			this.modelsBrowseDataService.fetchFamilyModelsAnalytics(this.familyId),
			this.applicationStateStoreProxyService.getPerformanceState(),
			this.modelsBrowseDataService.getFamilyNameId(this.familyId)
		])
			.pipe(
				first(([modelId, familyPerformanceStoreConfig, familyId, performanceState, familyNameId]) => !!modelId && !familyPerformanceStoreConfig.isLoading),
				switchMap(([modelId, familyPerformanceStoreConfig, familyId, performanceState, familyNameId]) => {
					const api$: Observable<any>[] = [
						this.modelsBrowseDataService.checkPerformanceDataValidity(this.familyId, performanceState?.customAdvisoryFee),
						this.marketViewConfigService.fetchConfig(familyNameId)
					];
					if (this.isEsgFamily && this.showEsgPdfPages) {
						api$.push(this.modelsBrowseDataService.fetchESGModelsAnalytics(this.familyId));
					}
					return combineLatest(api$);
				}),
				first()
			)
			.subscribe(([isPerformanceDataValid]) => {
				this.storeService.upsertOne(ModelsBrowseStoreConfig.reportConfig, {
					reportType: ViewCategory.FA_SELF_SERVICE_PDF,
					enableModularity: this.showPdfModularity && (this.isBlkModel || this.showCmsPdfModularity),
					isPerformanceDataValid: isPerformanceDataValid,
					yieldVisibility: { hideYield: true },
					isBlkModel: this.isBlkModel
				});
				this.urlRedirectService.redirectToGenerateReportPage();
			});
	}

	public onDownloadHoldingsClick(familyType: string): void {
		this.tealiumAnalyticsService.triggerToolCompletion(null, TealiumConstants.TOOL_NAMES.MODELS_EXPERIENCE, TealiumConstants.MODELS_EXPERIENCE_EVENTS.DOWNLOAD_HOLDINGS);
		this.modelsHoldingsExcelDownloadService.downloadHoldings(this.familyId, false, familyType).pipe(first()).subscribe();
	}

	private showError(): void {
		const failedMessage = PopupDialogObject.create({
			success: false,
			title: this.contentService.get('MB_SHORT_NAME_ERROR_HEADER', 'Something went wrong'),
			description: this.contentService.get('MB_SHORT_NAME_ERROR_MESSAGE', 'An error occurred while retrieving data. Please try again.'),
			actions: [PopupDialogAction.create({
				label: this.contentService.get('MB_RETRY_MESSAGE', 'Retry'),
				reload: true
			} as PopupDialogActionOptions)]
		} as PopupDialogOptions);
		this.errorPopupService.openResponseMessageDialog(failedMessage);
	}
}
